/**
 * The widget's phone field (boona docs/codebase/clients/phone-numbers.md; owner 2026-10-21): the
 * salon's country preselected — its flag and calling code, which can't be erased — and the number
 * typed the way people know it (05… in Riyadh, 8 029 … in Minsk); the flag menu takes a number from
 * anywhere else. The value is always international (+966501234567), the form the server stores.
 * It is the journal's field (src/components/custom/appointment-modal-create), for clients.
 *
 * Flags load from the widget's own host (/flags/3x2/XX.svg, copied from country-flag-icons at build —
 * vite.widget.config.ts), never a third-party CDN; country names come from the browser
 * (Intl.DisplayNames), in the widget's language. The library's stylesheet is pulled into
 * styles/widget.css, the one sheet inside the widget's shadow root.
 */
import * as React from "react";
import PhoneInput, { getCountries, parsePhoneNumber } from "react-phone-number-input";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { salonPhoneCountry } from "@/lib/phone/salonCountry";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";

export const FLAG_URL = "/flags/3x2/{XX}.svg";

/** Country names in `language` (the browser knows them all); the ISO code where it can't say. */
export function countryLabels(language: string): Record<string, string> {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([language, "en"], { type: "region" });
  } catch {
    names = null;
  }
  const out: Record<string, string> = {};
  for (const code of getCountries()) {
    let name: string | undefined;
    try {
      name = names?.of(code);
    } catch {
      name = undefined;
    }
    out[code] = name || code;
  }
  return out;
}

/** A valid number in its one international form: +375 80 29… → +37529…, +966 05… → +9665… (the field
 *  keeps a typed trunk prefix after a locked code; the server reads it the same way). Null when invalid. */
export function canonicalPhone(value: string): string | null {
  try {
    const parsed = value ? parsePhoneNumber(value) : undefined;
    return parsed && parsed.isValid() ? parsed.number : null;
  } catch {
    return null;
  }
}

/** The salon's country for the field: settings `country_iso2`, else its calling code's main country. */
export function salonCountryOfSettings() {
  const settings = getCompanyRegistry().getSettings();
  return salonPhoneCountry(settings?.country_iso2, settings?.phone_code);
}

type Props = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
  id?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
};

export function PhoneField({ value, onChange, onBlur, invalid, id, autoFocus, disabled, className }: Props) {
  const { t, i18n } = useTranslation();
  const country = salonCountryOfSettings();
  const labels = React.useMemo(
    () => ({
      ...countryLabels(i18n.language || "en"),
      country: t("widget.common.phone_country", "Phone number country"),
      phone: t("widget.common.phone", "Phone"),
    }),
    [i18n.language, t],
  );
  return (
    // a phone number reads left to right in every language
    <div dir="ltr" className={className}>
      <PhoneInput
        // the journal's settings: the code shows and stays; another country is picked from the flag menu
        // (a typed or pasted +44… is not taken over a locked +966 — tested 2026-10-21)
        international
        defaultCountry={country}
        countryCallingCodeEditable={false}
        addInternationalOption={false}
        countryOptionsOrder={[country, "|", "..."]}
        labels={labels}
        flagUrl={FLAG_URL}
        // the field holds +… only; anything older (a draft typed before this field) starts empty
        value={value && value.startsWith("+") ? value : undefined}
        onChange={(next) => onChange(next ?? "")}
        onBlur={() => {
          // leaving the field shows the number as it will be saved (8 029 … after +375 → +375 29 …)
          const canonical = canonicalPhone(value);
          if (canonical && canonical !== value) onChange(canonical);
          onBlur?.();
        }}
        id={id}
        autoFocus={autoFocus}
        disabled={disabled}
        autoComplete="tel"
        aria-invalid={invalid || undefined}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background text-base md:text-sm ring-offset-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          "[&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:min-w-0 [&_.PhoneInputInput]:flex-1",
          "[&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:px-2 [&_.PhoneInputInput]:outline-none",
          "[&_.PhoneInputCountry]:pl-3 [&_.PhoneInputCountry]:pr-1",
          invalid && "border-destructive",
          disabled && "cursor-not-allowed opacity-50",
        )}
      />
    </div>
  );
}
